import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BugSplat } from 'bugsplat-ng';
import { AuthService } from '../auth/auth.service';
import { CapacitorService } from '../capacitor/capacitor.service';
import { LoggingService } from '../logging/logging.service';
import { WindowService } from '../window/window.service';
import { isEnrichedError } from './enriched-error';

@Injectable()
export class MyAngularErrorHandler implements ErrorHandler {
  private authService = inject(AuthService);
  private bugsplat = inject(BugSplat);
  private capacitorService = inject(CapacitorService);
  private router = inject(Router);
  private windowService = inject(WindowService);
  private loggingService = inject(LoggingService);

  constructor() {
    // TODO BG add to bugsplat
    this.windowService
      .getWindow()
      .addEventListener('unhandledrejection', (event) => {
        this.handleError(event.reason as Error);
      });
  }

  async handleError(error: unknown): Promise<void> {
    // Log the error for debugging
    this.loggingService.error('Uncaught error', error);

    // Convert non-Error objects to proper Error instances
    let errorToReport: Error;

    if (error instanceof HttpErrorResponse) {
      errorToReport = new Error(error.error.message ?? 'Unknown network error');
    } else if (error instanceof Error) {
      errorToReport = error;
    } else if (typeof error === 'object' && error !== null) {
      // Handle [object Object] case
      try {
        const serialized = JSON.stringify(
          error,
          Object.getOwnPropertyNames(error),
        );
        errorToReport = new Error(`Non-Error object thrown: ${serialized}`);
        if (error instanceof Object) {
          Object.assign(errorToReport, { originalError: error });
        }
      } catch (jsonError) {
        errorToReport = new Error(
          `Non-serializable error object: ${Object.prototype.toString.call(error)}`,
        );
        if (error instanceof Object) {
          Object.assign(errorToReport, { originalError: error });
        }
      }
    } else {
      errorToReport = new Error(`Non-Error value thrown: ${String(error)}`);
    }

    const sessionUser = await this.authService.getSessionUser();
    const activeUserId = this.getActiveUserId();

    // Get recent logs (last 50 entries)
    const recentLogs = this.loggingService.getLogs().slice(-50);

    // Attach context from enriched errors
    const context = isEnrichedError(error) ? error.context : {};

    const attributes: Record<string, unknown> = {
      'Active User ID': activeUserId,
      'Session User ID': sessionUser?.id,
      ...context,
    };

    return this.bugsplat.post(errorToReport, {
      appKey: this.capacitorService.platform,
      email: sessionUser?.email,
      user: sessionUser?.id,
      description: `Active User ID: ${activeUserId}, Session User ID: ${sessionUser?.id}`,
      additionalFormDataParams: [
        {
          key: 'attributes',
          value: JSON.stringify(attributes),
        },
        {
          key: 'file0',
          value: new Blob([
            recentLogs
              .map(
                (l) => `${l.timestamp.toISOString()} ${l.level}: ${l.message}`,
              )
              .join('\n'),
          ]),
          filename: 'logs.txt',
        },
      ],
    });
  }

  private getActiveUserId(): string {
    let current = this.router.routerState?.snapshot?.root;
    while (current?.firstChild) {
      current = current.firstChild;
    }
    return current?.paramMap.get('userId') ?? '';
  }
}

@Injectable()
export class NullErrorHandler implements ErrorHandler {
  private loggingService = inject(LoggingService);

  handleError(error: Error) {
    this.loggingService.error(error.message, error);
  }
}
